import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Flex,
  ModalBody,
  Spacer,
  useToast,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { EditorState, convertToRaw } from 'draft-js';
import * as React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { useUpdateCacheOnPostCreation } from '@/components/PostingExperience/useUpdateCacheOnPostCreation';
import { SchedulePostMenu } from '@/components/post-form/SchedulePostMenu';
import { SimplePostForm } from '@/components/post-form/SimplePostForm';
import { useAiTagging } from '@/hooks/useAiTagging';
import { usePermissions } from '@/hooks/usePermissions';
import { logError } from '@/imports/logging/ClientLogger';
import { post_created, analyzeContent } from '@/analytics';
import { AnnouncePostModal } from '@/imports/ui/components/feed-item/AnnouncePostModal';

import {
  CreatePostModal_CreateSimplePost,
  CreatePostModal_CreateSimplePostVariables,
} from './graphql-types/CreatePostModal_CreateSimplePost';

import { PostingModalFooter } from '../PostingModalFooter';

const TAGS_FOR_AI = gql`
  query CreatePostModal_TagsForAi($input: MultiTagInput) {
    tagsWithSearchTerm(input: $input) {
      tags {
        _id
        name
      }
    }
  }
`;

const CREATE_POST = gql`
  mutation CreatePostModal_CreateSimplePost($input: CreateSimplePostInput!) {
    createSimplePost(input: $input) {
      data {
        ...UseUpdateCacheOnPostCreation_PostV2Fragment
        author {
          _id
          userOnboardingChecklistState {
            userHasPosted
          }
        }
        group {
          _id
          isGlobalSingleton
        }
      }
    }
  }
  ${useUpdateCacheOnPostCreation.postFragment}
`;

type ICreatePostModalProps = {
  onClose: () => void;
  createArticle: () => void;
  group?: { _id: string };
  organization?: { _id: string };
  defaultIsSponsoredPost?: boolean;
  tags?: { _id: string; name: string }[];
};

type SimplePostFormValues = {
  title: string;
  group: string;
  images: ({ imageId: string } | { videoId: string })[];
  body: EditorState;
  isSponsoredPost: boolean;
  isAnnouncement: boolean;
  tags?: { _id: string; name: string }[];
  attachments: { versionFileId: string; fileName: string }[];
  scheduledPublishDate: Date | null;
};

export const CreatePostModal: React.FC<ICreatePostModalProps> = (props) => {
  const [isAnnounceModalOpen, setIsAnnounceModalOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const permissions = usePermissions();
  const canSchedulePosts = permissions.canDoAdminStuffAndAuthenticated;

  const toast = useToast();

  const methods = useForm<SimplePostFormValues>({
    defaultValues: {
      title: '',
      group: props.group?._id,
      images: [],
      body: EditorState.createEmpty(),
      isSponsoredPost: !!props.defaultIsSponsoredPost,
      isAnnouncement: false,
      tags: (props.tags || []).filter((t) => !!t?._id && !!t?.name),
      attachments: [],
      scheduledPublishDate: null,
    },
  });
  const { handleSubmit, formState, watch, setValue, getValues } = methods;

  const isAnnouncement = watch('isAnnouncement');
  const scheduledPublishDate = watch('scheduledPublishDate');
  const title = watch('title');
  const body = watch('body');

  const { data: tagsData } = useQuery(TAGS_FOR_AI, {
    variables: {
      input: { limit: 500, offset: 0, filter: { searchText: '' } },
    },
  });
  const aiLabels = tagsData?.tagsWithSearchTerm?.tags ?? [];

  const { recommendedHashtags } = useAiTagging({
    title,
    bodyPlainText: body?.getCurrentContent?.()?.getPlainText?.() ?? '',
    labels: aiLabels,
    enabled: true,
    onResult: React.useCallback(
      ({ selectedHashtags }) => {
        const current = (getValues('tags') ?? []).filter(
          (t: { _id?: string; name?: string }) => !!t?._id && !!t?.name
        );
        const currentIds = new Set(current.map((t: { _id: string }) => t._id));
        const fromAi = selectedHashtags
          .filter((t) => !!t?._id && !!t?.name)
          .filter((t) => !currentIds.has(t._id));
        if (fromAi.length > 0) {
          setValue('tags', [...current, ...fromAi], {
            shouldDirty: true,
            shouldTouch: true,
          });
        }
      },
      [getValues, setValue]
    ),
  });

  const [createPost] = useMutation<
    CreatePostModal_CreateSimplePost,
    CreatePostModal_CreateSimplePostVariables
  >(CREATE_POST, { refetchQueries: ['AccountMenu_Query'] });

  const { updateCacheOnPostCreation } = useUpdateCacheOnPostCreation();

  const onSubmit = async (params: SimplePostFormValues) => {
    setLoading(true);

    try {
      const result = await createPost({
        variables: {
          input: {
            post: {
              title: params.title,
              body: convertToRaw(params.body.getCurrentContent()),
              versionFileIds: params.attachments.map((attachment) => {
                return attachment.versionFileId;
              }),
              tagIds: (params.tags ?? [])
                .map((tag) => tag?._id)
                .filter((id): id is string => !!id),
              embeddedImageIds: params.images
                .filter((id): id is { imageId: string } => 'imageId' in id)
                .map((id) => id.imageId),
              ...(recommendedHashtags.length > 0
                ? { recommendedHashtags }
                : {}),
              ...(props.organization
                ? params.isSponsoredPost
                  ? {
                      sponsorData: {
                        isSponsoredPost: true,
                        audience: { groupId: params.group },
                      },
                      organizationId: props.organization._id,
                    }
                  : { organizationId: props.organization._id }
                : { groupId: params.group }),
              scheduledPublishDate: params.scheduledPublishDate?.toISOString(),
            },
            isAnnouncement: params.isAnnouncement,
          },
        },
        update(cache, { data }) {
          if (!data?.createSimplePost.data) {
            return;
          }
          updateCacheOnPostCreation({
            cache,
            post: data.createSimplePost.data,
          });
        },
      });
      const bodyText = params.body.getCurrentContent().getPlainText();
      const contentAnalysis = analyzeContent(bodyText);

      post_created({
        entity_type: 'post',
        entity_id: result.data?.createSimplePost.data._id,
        location_posted_in: props.organization
          ? 'organization'
          : result.data?.createSimplePost.data.group?.isGlobalSingleton
          ? 'home'
          : 'group',
        location_group_id: !props.organization ? params.group : undefined,
        location_organization_id: props.organization?._id,
        content_text: contentAnalysis.contentText,
        content_length: contentAnalysis.contentLength,
        content_word_count: contentAnalysis.wordCount,
        content_has_question_mark: contentAnalysis.hasQuestionMark,
        content_has_links: contentAnalysis.hasLinks,
        content_link_count: contentAnalysis.linkCount,
        content_mention_count: contentAnalysis.mentionCount,
        content_hashtag_count: contentAnalysis.hashtagCount,
        content_hashtags: contentAnalysis.hashtags,
        content_has_media:
          params.images.length > 0 || params.attachments.length > 0,
        content_media_count: params.images.length + params.attachments.length,
        content_has_tags: !!params.tags && params.tags.length > 0,
        content_type: 'post',
        post_type: 'message',
        post_is_announcement: params.isAnnouncement,
        post_is_scheduled: !!params.scheduledPublishDate,
      });
      props.onClose();

      !!params.scheduledPublishDate &&
        toast({
          description: `Post scheduled for ${dayjs(
            params.scheduledPublishDate
          ).format('MMM D, h:mm A')}`,
          status: 'success',
        });
    } catch (err) {
      toast({
        status: 'error',
        description: err.message ?? 'Failed to create post.',
      });
      logError(err);
    } finally {
      setLoading(false);
    }
  };

  const onInvalid = () => {
    /**
     * If the form is invalid, reset `scheduledPublishDate`
     * User should be able to click `Post` to create the post
     * immediately after the form fields become valid
     */
    methods.resetField('scheduledPublishDate');
  };

  return (
    <FormProvider {...methods}>
      <AnnouncePostModal
        open={isAnnounceModalOpen}
        onClose={() => setIsAnnounceModalOpen(false)}
        onClickAnnounce={() => {
          handleSubmit(onSubmit, onInvalid)();
          setIsAnnounceModalOpen(false);
        }}
        onClickPost={() => {
          methods.setValue('isAnnouncement', false);
          handleSubmit(onSubmit, onInvalid)();
          setIsAnnounceModalOpen(false);
        }}
        announcementLoading={false}
        loading={false}
      />

      <ModalBody px="6" py="4">
        <SimplePostForm
          organizationId={props.organization?._id}
          initialGroupId={props.group?._id}
        />
      </ModalBody>

      <PostingModalFooter>
        <Flex w="100%" align="center">
          <Button
            variant="ghost"
            colorScheme="gray"
            onClick={props.onClose}
            display={{ base: 'inline-flex', md: 'inline-flex', lg: 'none' }}
            mr={2}
            border="1px solid"
            borderColor="gray.300"
          >
            Cancel
          </Button>
          <Spacer />
          {canSchedulePosts && (
            <Controller
              name="scheduledPublishDate"
              render={({ field: { onBlur, onChange, ref, value, name } }) => (
                <Box mr={2}>
                  <SchedulePostMenu
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    isDisabled={
                      (formState.isSubmitted && !formState.isValid) ||
                      loading
                    }
                    validate={() => {
                      if (!value) {
                        methods.setError(name, {
                          message: 'A date and time must be specified',
                        });
                        return false;
                      } else {
                        methods.clearErrors(name);
                        return true;
                      }
                    }}
                    validationMessage={
                      formState.errors.scheduledPublishDate?.message
                    }
                  />
                </Box>
              )}
            />
          )}
          <Button
            isLoading={loading}
            isDisabled={formState.isSubmitted && !formState.isValid}
            onClick={
              isAnnouncement
                ? () => setIsAnnounceModalOpen(true)
                : handleSubmit(onSubmit, onInvalid)
            }
          >
            {scheduledPublishDate ? 'Schedule Post' : 'Post Now'}
          </Button>
        </Flex>
      </PostingModalFooter>
    </FormProvider>
  );
};
